/**
 * The Review tab: unsure reads, waiting for a human.
 *
 * Each card is one product whose Open Food Facts lookup produced candidates
 * but no certainty. The options are radios, there is always a custom size
 * field for when every candidate is wrong, and "none of these" is a
 * first-class answer that stops the asking. Decisions are recorded on the
 * server, applied on the PC at the next fetch, and the unit prices they
 * unlock appear one publish later - the card says so rather than pretending.
 *
 * Nothing decided here changes a figure directly: confirmed sizes only ever
 * reach the screen through publish.py's unit-price computation.
 */
import { useState } from "preact/hooks";

import type { ProductMetaEdit } from "./api.js";
import type { Summary } from "./contracts/index.js";
import { money } from "./format.js";

type Suggestion = NonNullable<Summary["meta_suggestions"]>[number];

const UNITS = ["ml", "l", "g", "kg", "each"] as const;

function SuggestionCard({
  s,
  decided,
  onDecide,
}: {
  s: Suggestion;
  decided: ProductMetaEdit | undefined;
  onDecide: (edit: ProductMetaEdit) => void | Promise<void>;
}) {
  const [choice, setChoice] = useState<string>("");
  const [customQty, setCustomQty] = useState("");
  const [customUnit, setCustomUnit] = useState<string>("ml");

  if (decided) {
    return (
      <div class="card review done">
        <p>
          <strong>{s.product}</strong>
          <span class="muted small">
            {" "}
            · {decided.status === "rejected" ? "marked unknown" : "confirmed"} - applies from the
            next run
          </span>
        </p>
      </div>
    );
  }

  const submit = (e: Event) => {
    e.preventDefault();
    if (choice === "custom") {
      const qty = Number(customQty);
      if (!Number.isFinite(qty) || qty <= 0) return;
      void onDecide({
        product: s.product,
        status: "confirmed",
        size_qty: qty,
        size_unit: customUnit,
      });
      return;
    }
    const c = s.candidates.find((x) => x.barcode === choice);
    if (!c) return;
    void onDecide({
      product: s.product,
      status: "confirmed",
      barcode: c.barcode,
      off_name: c.label,
      size_qty: c.size_qty ?? null,
      size_unit: c.size_unit ?? null,
    });
  };

  return (
    <form class="card review" onSubmit={submit}>
      <p>
        <strong>{s.product}</strong>
        {s.context.each !== null && s.context.each !== undefined && s.context.currency && (
          <span class="muted small">
            {" "}
            · last {money(s.context.each, s.context.currency)}
            {s.context.store ? ` at ${s.context.store}` : ""}
          </span>
        )}
      </p>
      <p class="muted small">Which product is this?</p>

      {s.candidates.map((c) => (
        <label class="option" key={c.barcode}>
          <input
            type="radio"
            name={`opt-${s.product}`}
            checked={choice === c.barcode}
            onChange={() => setChoice(c.barcode ?? "")}
          />
          <span>
            {c.label}
            {c.brands ? <span class="muted small"> · {c.brands}</span> : null}
            {c.size_qty && c.size_unit ? (
              <span class="muted small">
                {" "}
                · {c.size_qty}
                {c.size_unit}
              </span>
            ) : null}
          </span>
        </label>
      ))}

      <label class="option">
        <input
          type="radio"
          name={`opt-${s.product}`}
          checked={choice === "custom"}
          onChange={() => setChoice("custom")}
        />
        <span>None of these - I know the size:</span>
      </label>
      {choice === "custom" && (
        <div class="customsize">
          <input
            type="text"
            inputmode="decimal"
            placeholder="e.g. 400"
            value={customQty}
            onInput={(e) => setCustomQty((e.target as HTMLInputElement).value.replace(/[^\d.]/g, ""))}
            aria-label="Package size"
          />
          <select
            value={customUnit}
            onChange={(e) => setCustomUnit((e.target as HTMLSelectElement).value)}
            aria-label="Unit"
          >
            {UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
        </div>
      )}

      <div class="reviewactions">
        <button type="submit" class="primary" disabled={!choice}>
          Save
        </button>
        <button
          type="button"
          onClick={() => void onDecide({ product: s.product, status: "rejected" })}
        >
          Don't know / skip forever
        </button>
      </div>
    </form>
  );
}

export function Review({
  summary,
  edits,
  onDecide,
}: {
  summary: Summary | null;
  edits: ProductMetaEdit[];
  onDecide: (edit: ProductMetaEdit) => void | Promise<void>;
}) {
  const suggestions = summary?.meta_suggestions ?? [];
  const byProduct = new Map(edits.map((e) => [e.product.trim().toLowerCase(), e]));

  if (!suggestions.length) {
    return (
      <div class="review empty-state">
        <h2>Nothing to review</h2>
        <p class="muted">
          When a run looks up products against Open Food Facts and is not sure, the questions
          land here. Run <code>enrich.py</code> on your PC to start.
        </p>
      </div>
    );
  }

  const open = suggestions.filter((s) => !byProduct.has(s.product.trim().toLowerCase()));
  const done = suggestions.filter((s) => byProduct.has(s.product.trim().toLowerCase()));

  return (
    <div class="reviewscreen">
      <p class="muted small">
        Confirming a product unlocks its unit price (€/L, €/kg). Decisions apply from the next
        run - nothing changes a number silently.
      </p>
      {open.map((s) => (
        <SuggestionCard key={s.product} s={s} decided={undefined} onDecide={onDecide} />
      ))}
      {done.map((s) => (
        <SuggestionCard
          key={s.product}
          s={s}
          decided={byProduct.get(s.product.trim().toLowerCase())}
          onDecide={onDecide}
        />
      ))}
    </div>
  );
}
